import { QrcodeOutlined } from '@ant-design/icons';
import { ApiError } from '@hq/api-sdk';
import { QRCodeSVG } from '@rc-component/qrcode';
import { Alert, Button, Modal, Space, Spin, Typography } from 'antd';
import { useCallback, useEffect, useRef, useState } from 'react';
import { config, hq } from '../session';

type Phase = 'LOADING' | 'READY' | 'EXPIRED' | 'ERROR';

/** 二维码指向的站点根：优先用部署配置（手机可达的域名），否则用当前访问地址。 */
function baseUrl(): string {
  return (config.mobileEntryUrl?.trim() || window.location.origin).replace(/\/+$/, '');
}

/** 落在 localhost 时手机访问不到，需要在 config.js 里配对外地址。 */
function isLocalOnly(url: string): boolean {
  return /^https?:\/\/(localhost|127\.0\.0\.1|\[::1\])(:|\/|$)/i.test(url);
}

/**
 * 手机打开面板：签发一次性票据 → 展示二维码 → 手机浏览器打开即带登录态。
 * 电脑端不需要轮询：票据被兑换后手机端自己就进去了。
 */
function QrPanel() {
  const [ticket, setTicket] = useState<string | null>(null);
  const [phase, setPhase] = useState<Phase>('LOADING');
  const [leftSeconds, setLeftSeconds] = useState(0);
  const [error, setError] = useState<string | null>(null);

  const create = useCallback(async () => {
    setPhase('LOADING');
    setError(null);
    setTicket(null);
    try {
      const result = await hq.auth.qrShare.create();
      setTicket(result.ticket);
      setLeftSeconds(Math.max(1, result.expireSeconds));
      setPhase('READY');
    } catch (err) {
      setPhase('ERROR');
      setError(err instanceof ApiError ? err.friendlyMessage : String(err));
    }
  }, []);

  // 首次打开面板即签发（ref 守卫：StrictMode 下 effect 跑两次，避免生成两张票据）
  const startedRef = useRef(false);
  useEffect(() => {
    if (startedRef.current) return;
    startedRef.current = true;
    void create();
  }, [create]);

  useEffect(() => {
    if (phase !== 'READY') return undefined;
    const id = window.setInterval(() => setLeftSeconds((value) => Math.max(0, value - 1)), 1000);
    return () => window.clearInterval(id);
  }, [phase]);

  useEffect(() => {
    if (phase === 'READY' && leftSeconds <= 0) setPhase('EXPIRED');
  }, [phase, leftSeconds]);

  const url = ticket ? `${baseUrl()}/m-enter?ticket=${encodeURIComponent(ticket)}` : '';
  const localOnly = isLocalOnly(baseUrl());

  return (
    <div style={{ textAlign: 'center' }}>
      <div
        style={{
          width: 208,
          height: 208,
          margin: '0 auto 12px',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          background: '#fff',
          border: '1px solid #f0f0f0',
          borderRadius: 12,
          // 过期/出错时压住二维码，避免扫到一张已作废的码
          opacity: phase === 'READY' ? 1 : 0.15
        }}
      >
        {url ? (
          <QRCodeSVG
            value={url}
            size={192}
            level="M"
            marginSize={2}
            bgColor="#ffffff"
            fgColor="#000000"
            title="手机打开二维码"
            style={{ width: '100%', height: 'auto', display: 'block', padding: 4 }}
          />
        ) : phase === 'LOADING' ? (
          <Spin />
        ) : null}
      </div>

      {phase === 'LOADING' ? (
        <Typography.Paragraph type="secondary" style={{ marginBottom: 0 }}>
          正在生成二维码…
        </Typography.Paragraph>
      ) : null}

      {phase === 'READY' ? (
        <>
          <Typography.Paragraph style={{ marginBottom: 4 }}>
            手机浏览器扫码，直接以当前账号打开
          </Typography.Paragraph>
          <Typography.Paragraph type="secondary" style={{ fontSize: 12, marginBottom: 0 }}>
            二维码 {leftSeconds} 秒后失效 · 一次性使用
          </Typography.Paragraph>
        </>
      ) : null}

      {phase === 'EXPIRED' ? (
        <>
          <Typography.Paragraph type="secondary" style={{ marginBottom: 12 }}>
            二维码已过期，请刷新后重新扫码
          </Typography.Paragraph>
          <Button type="primary" onClick={() => void create()}>
            刷新二维码
          </Button>
        </>
      ) : null}

      {phase === 'ERROR' ? (
        <>
          <Alert type="error" showIcon message={error ?? '生成二维码失败'} style={{ textAlign: 'left' }} />
          <Space style={{ marginTop: 12 }}>
            <Button type="primary" onClick={() => void create()}>
              重试
            </Button>
          </Space>
        </>
      ) : null}

      {phase === 'READY' ? (
        <Space direction="vertical" size={8} style={{ width: '100%', marginTop: 12 }}>
          {localOnly ? (
            <Alert
              type="warning"
              showIcon
              style={{ textAlign: 'left', fontSize: 12 }}
              message="当前页面是 localhost，手机可能打不开"
              description="请在 public/config.js 里把 mobileEntryUrl 配置成手机可访问的域名/局域网地址，或改用该地址访问本站。"
            />
          ) : null}
          <Typography.Paragraph
            copyable={{ text: url, tooltips: ['复制链接', '已复制'] }}
            type="secondary"
            style={{ fontSize: 11, marginBottom: 0, wordBreak: 'break-all' }}
          >
            {url}
          </Typography.Paragraph>
          <Button size="small" type="text" onClick={() => void create()}>
            刷新
          </Button>
        </Space>
      ) : null}
    </div>
  );
}

/** 顶栏「手机打开」：点开弹窗出二维码，手机扫码即带当前登录态进入本站点。 */
export default function MobileQrEntry() {
  const [open, setOpen] = useState(false);

  return (
    <>
      <Button type="text" icon={<QrcodeOutlined />} onClick={() => setOpen(true)}>
        手机打开
      </Button>
      <Modal
        open={open}
        onCancel={() => setOpen(false)}
        footer={null}
        width={400}
        title="手机打开"
        centered
        // 关闭即卸载面板：让票据自然过期，不在屏幕上留可用二维码
        destroyOnHidden
      >
        <QrPanel />
      </Modal>
    </>
  );
}
